import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { DoubleArrowDownIcon } from "@radix-ui/react-icons";

import { useStatusFilter } from "./hooks/use-status-filter";
import { getAllAllowanceRequestsQueryOptions } from "@/config/queries/query-options";

import { Label } from "@/components/ui/label/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group/radio-group";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { Button } from "@/components/ui/button/button";
import { Spinner } from "@/components/ui/spinner/spinner";
import { Separator } from "@/components/ui/separator/separator";
import { DataTable } from "./_components/allowance-table/data-table";
import { columns } from "./_components/allowance-table/columns";
import { ViewAllowanceModal } from "./_components/view-allowance-modal/view-allowance-modal";
import { DeleteAllowanceModal } from "./_components/delete-allowance-modal/delete-allowance-modal";

const AdminAllowancePage = () => {
  const adminKey = Cookies.get("key") as string;
  const { filter, setFilter } = useStatusFilter();

  const { data, isPending, isFetching } = useQuery(
    getAllAllowanceRequestsQueryOptions(adminKey, filter)
  );

  const allowancesData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.Allowances === undefined) {
      return [];
    }

    const result = data?.Allowances?.map((la) => {
      return {
        id: la?._id,
        reason: la?.reason,
        amountRequested: la?.amountRequested,
        status: la?.status,
        employeeName: la?.employee?.name,
        employeeEmail: la?.employee?.email,
        createdAt: la?.createdAt as string | Date,
        attachment: la?.attachment,
      };
    });

    return result?.sort(
      (a, b) =>
        new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
    );
  }, [isPending, isFetching, data]);

  return (
    <div className="p-5 h-[calc(100%-56px)]">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-medium">Allowance</h3>

        <Popover>
          <PopoverTrigger>
            <Button
              className="mr-3 flex items-center gap-2"
              size="sm"
              variant="outline"
            >
              <DoubleArrowDownIcon className="w-4 h-4" />
              Filter
              {filter && (
                <>
                  <Separator orientation="vertical" />
                  {filter} ({allowancesData?.length})
                </>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <RadioGroup
              value={filter}
              onValueChange={(
                value: "Pending" | "Approved" | "Rejected" | "Cancelled"
              ) => {
                setFilter(value);
              }}
            >
              <div className="flex items-center space-x-2 cursor-pointer w-fit">
                <RadioGroupItem value="Pending" id="pending" />
                <Label htmlFor="pending">Pending</Label>
              </div>
              <div className="flex items-center space-x-2 cursor-pointer w-fit">
                <RadioGroupItem value="Approved" id="approved" />
                <Label htmlFor="approved">Approved</Label>
              </div>
              <div className="flex items-center space-x-2 cursor-pointer w-fit">
                <RadioGroupItem value="Rejected" id="rejected" />
                <Label htmlFor="rejected">Rejected</Label>
              </div>
              <div className="flex items-center space-x-2 cursor-pointer w-fit">
                <RadioGroupItem value="Cancelled" id="cancelled" />
                <Label htmlFor="cancelled">Cancelled</Label>
              </div>
            </RadioGroup>
          </PopoverContent>
        </Popover>
      </div>

      {isPending ? (
        <div className="h-20 mt-10 flex items-center justify-center">
          <Spinner size="small">
            <p>Searching for Leave Applications...</p>
          </Spinner>
        </div>
      ) : data?.Allowances?.length ? (
        <div className="mt-5">
          <DataTable data={allowancesData} columns={columns} />
        </div>
      ) : (
        <div className="border h-20 flex items-center justify-center mt-5">
          No {filter} Allowances
        </div>
      )}

      <ViewAllowanceModal />
      <DeleteAllowanceModal />
    </div>
  );
};

export default AdminAllowancePage;
